import React, { ReactElement, useEffect, useState } from 'react';
import { PaginationItem } from '../PaginationItem';
import { Icon } from '../../../Icon';
import {
  determineEllipsisLocation,
  determineMiddleLinksConfig,
} from '../../helpers';

export const MAX_NUM_LINKS = 5;
export const MAX_NUM_MIDDLE_LINKS = 3;
export const DEFAULT_FIRST_MIDDLE_VAL = 2;

type SizeTypes = 'small' | 'default' | 'large';
export type EllipsisPositions = 'left' | 'right' | 'both' | 'none';
export type MiddleLinksConfigType = [number, number];

export interface PaginationProps {
  size?: SizeTypes;
  rounded?: boolean;
  current: number;
  pages: number;
  onClick: (page: number) => void;
}

const defaultProps: Partial<PaginationProps> = {
  rounded: false,
  size: 'default',
};
export function Pagination({
  current,
  pages,
  onClick,
  ...rest
}: PaginationProps): ReactElement | null {
  const [showEllipsis, setShowEllipses] = useState<EllipsisPositions>('none');
  const [middleButtonValues, setMiddleButtonValues] =
    useState<MiddleLinksConfigType>([0, 0]);

  useEffect(() => {
    setShowEllipses(determineEllipsisLocation(current, pages));
    setMiddleButtonValues(
      determineMiddleLinksConfig(showEllipsis, current, pages),
    );
  }, [current, pages, showEllipsis]);

  const renderMiddleButtons = () => {
    const [first, numOfButtons] = middleButtonValues;

    return Array(numOfButtons)
      .fill(0)
      .map((_, i) => (
        <PaginationItem
          {...rest}
          key={i}
          value={first + i}
          selected={first + i === current}
          onClick={() => onClick(first + i)}
        />
      ));
  };

  if (pages < current) {
    return null;
  }

  return (
    <ul className="pagination">
      <PaginationItem
        {...rest}
        value="Previous"
        selected={false}
        disabled={current <= 1}
        onClick={() => onClick(current - 1)}
      />
      <PaginationItem
        {...rest}
        value={1}
        selected={current === 1}
        onClick={() => onClick(1)}
      />
      {(showEllipsis === 'left' || showEllipsis === 'both') && (
        <li>
          <Icon name="more" />
        </li>
      )}
      {renderMiddleButtons()}
      {(showEllipsis === 'right' || showEllipsis === 'both') && (
        <li>
          <Icon name="more" />
        </li>
      )}
      {pages > 1 && (
        <PaginationItem
          {...rest}
          value={pages}
          selected={current === pages}
          onClick={() => onClick(pages)}
        />
      )}
      <PaginationItem
        {...rest}
        value="Next"
        selected={false}
        disabled={current === pages}
        onClick={() => onClick(current + 1)}
      />
    </ul>
  );
}

Pagination.defaultProps = defaultProps;
